import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete,
    type Key,
    ListBox,
    SearchField,
    Tag,
    TagGroup,
    useFilter,
} from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import {
    CATEGORY_FILTER_SHORTCUT,
    ShortcutHint,
    toAriaKeyShortcuts,
    useShortcut,
} from '@/components/shortcuts';
import { useSeller } from '../../useSeller';
import { useCategoryOptions } from './useCategoryOptions';
import { useCategoryFilterOpenState } from './useCategoryFilterOpenState';
import { CategoryFilterPopoverExit } from './CategoryFilterPopoverExit';
import { CategoryFilterScope } from './CategoryFilterScope';

type CategoryFilterProps = {
    /** Called once the popover closes, by any route — `ProductPanel` sends focus home. */
    onClose?: () => void;
};

/**
 * Category filter row: a compact multi-select, type-to-filter dropdown whose
 * trigger only ever shows the "Filter by category" label, plus the active
 * selections rendered as removable chips *beside* it (not inside the field) so
 * the control stays compact and the applied filters read clearly. An empty
 * selection means "all categories" (handled in `useSeller`).
 *
 * `F3` opens the popover from anywhere on the sale screen, and its hint sits
 * on the trigger, which names the key with `aria-keyshortcuts` (the design
 * system gives the trigger's inner button no prop for it); closing it by any route (`Escape`, an outside click, a
 * blur) sends focus home via `onClose`. Its own `Escape` handling (a plain
 * react-aria `Autocomplete` behaviour, not `useShortcut`) is what makes the
 * sale screen's own `Esc` layering treat it as "the open overlay":
 * `CategoryFilterScope` suspends the `seller` scope while it's open, so
 * `SALE_ESCAPE_SHORTCUT` never runs at the same time.
 *
 * The category list is loaded from the API (`GET /categories/picker` — every live
 * category, unpaged, translated by slug); loading and error states surface as
 * inline text inside the dropdown.
 */
export const CategoryFilter: React.FC<CategoryFilterProps> = ({ onClose }) => {
    const { t } = useTranslation('pages');
    const { selectedCategoryIds, setSelectedCategoryIds } = useSeller();
    const { categories, isLoading, isError } = useCategoryOptions();
    const { contains } = useFilter({ sensitivity: 'base' });
    const { isOpen, open, onOpenChange } = useCategoryFilterOpenState();

    useShortcut(CATEGORY_FILTER_SHORTCUT.id, open);

    const onRemoveTags = (keys: Set<Key>) =>
        setSelectedCategoryIds(
            selectedCategoryIds.filter((id) => !keys.has(id)),
        );

    const selectedCategories = selectedCategoryIds
        .map((id) => categories.find((category) => category.id === id))
        .filter((category) => category !== undefined);

    const emptyMessage = isLoading
        ? t('seller.loadingCategories')
        : isError
          ? t('seller.categoriesError')
          : t('seller.noCategories');

    return (
        <div
            className="flex min-w-0 items-center gap-3"
            {...getNodeTestIdProp('seller-category-filter')}
        >
            {isOpen ? <CategoryFilterScope /> : null}
            <Autocomplete
                aria-label={t('seller.filterCategories')}
                className="w-56 flex-shrink-0"
                isOpen={isOpen}
                onChange={(keys) =>
                    setSelectedCategoryIds((keys as Key[]).map(String))
                }
                onOpenChange={onOpenChange}
                placeholder={t('seller.filterCategories')}
                selectionMode="multiple"
                value={selectedCategoryIds}
            >
                <Autocomplete.Trigger
                    aria-keyshortcuts={toAriaKeyShortcuts(
                        CATEGORY_FILTER_SHORTCUT.combination,
                    )}
                >
                    <Autocomplete.Value>
                        {() => t('seller.filterCategories')}
                    </Autocomplete.Value>
                    <ShortcutHint
                        className="ms-2 flex-shrink-0 self-center"
                        placement="control"
                        shortcut={CATEGORY_FILTER_SHORTCUT}
                    />
                    <Autocomplete.Indicator />
                </Autocomplete.Trigger>
                <Autocomplete.Popover>
                    <CategoryFilterPopoverExit
                        isOpen={isOpen}
                        onExited={onClose}
                    />
                    <Autocomplete.Filter filter={contains}>
                        <SearchField
                            autoFocus
                            name="category-search"
                            variant="secondary"
                        >
                            <SearchField.Group>
                                <SearchField.SearchIcon />
                                <SearchField.Input
                                    placeholder={t('seller.searchCategory')}
                                />
                                <SearchField.ClearButton />
                            </SearchField.Group>
                        </SearchField>
                        <ListBox
                            renderEmptyState={() => (
                                <div className="p-3 text-center text-sm text-muted">
                                    {emptyMessage}
                                </div>
                            )}
                        >
                            {categories.map((category) => (
                                <ListBox.Item
                                    id={category.id}
                                    key={category.id}
                                    textValue={category.name}
                                >
                                    {category.name}
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            ))}
                        </ListBox>
                    </Autocomplete.Filter>
                </Autocomplete.Popover>
            </Autocomplete>

            {selectedCategories.length > 0 ? (
                <div
                    className="min-w-0 flex-1 overflow-x-auto"
                    {...getNodeTestIdProp('seller-category-chips')}
                >
                    <TagGroup
                        aria-label={t('seller.filterCategories')}
                        onRemove={onRemoveTags}
                        size="sm"
                    >
                        <TagGroup.List className="flex-nowrap">
                            {selectedCategories.map((category) => (
                                <Tag id={category.id} key={category.id}>
                                    {category.name}
                                </Tag>
                            ))}
                        </TagGroup.List>
                    </TagGroup>
                </div>
            ) : null}
        </div>
    );
};
